/**
 * Account signup / sign-in → identified lead bridge.
 *
 * The popup path (`identifyLeadFromPopup`) only covers visitors who convert
 * through the WhatsApp-OTP popup. Visitors who create an account directly on
 * /auth (email + password, WhatsApp OTP sign-in, Google / Apple) never got a
 * `lead_profiles` row, so they were invisible in Admin → Leads.
 *
 * This server fn runs with the caller's own session (bearer token), so the
 * identity is proven by Supabase Auth — no caller-supplied phone/email is
 * trusted. It stamps the lead profile keyed by the caller's anonymous ID and
 * backfills prior anonymous events, exactly like the popup path.
 */
import { createServerFn } from "@tanstack/react-start";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";
import { z } from "zod";
import { normalizePhoneStr as normalizePhone } from "@/lib/phone";

const InputSchema = z.object({
  anonymousId: z.string().min(8).max(64),
});

export const identifyLeadFromAuth = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((data: { anonymousId: string }) => InputSchema.parse(data))
  .handler(async ({ data, context }) => {
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");

    // Authoritative identity from the validated session.
    const { data: userRes } = await context.supabase.auth.getUser();
    const user = userRes?.user;
    if (!user) return { ok: false as const, error: "unauthenticated" as const };

    const email = user.email?.toLowerCase() ?? null;
    const rawPhone =
      user.phone ||
      (user.user_metadata?.phone as string | undefined) ||
      (user.user_metadata?.phone_number as string | undefined) ||
      null;
    const phone = rawPhone ? normalizePhone(rawPhone) : null;
    const now = new Date().toISOString();

    // Link the customers row for this account when we can find one.
    let customerId: string | null = null;
    try {
      const orFilters = [
        `auth_user_id.eq.${user.id}`,
        ...(phone ? [`phone.eq.${phone}`] : []),
        ...(email ? [`email.eq.${email}`] : []),
      ].join(",");
      const { data: cust } = await supabaseAdmin
        .from("customers")
        .select("id")
        .or(orFilters)
        .limit(1)
        .maybeSingle();
      customerId = cust?.id ?? null;
    } catch (err) {
      console.warn("[lead-identify-auth] customer lookup failed", (err as Error).message);
    }

    const patch = {
      auth_user_id: user.id,
      identified: true,
      last_seen: now,
      ...(email ? { email } : {}),
      ...(phone ? { phone } : {}),
      // Supabase only issues a session after the relevant channel is
      // confirmed, so treat those as verification stamps.
      ...(user.email_confirmed_at ? { email_verified_at: user.email_confirmed_at } : {}),
      ...(user.phone_confirmed_at ? { phone_verified_at: user.phone_confirmed_at } : {}),
      ...(customerId ? { customer_id: customerId } : {}),
    };

    let profileId: string | null = null;

    const { data: profile, error: upErr } = await supabaseAdmin
      .from("lead_profiles")
      .upsert({ anonymous_id: data.anonymousId, ...patch } as never, {
        onConflict: "anonymous_id",
      })
      .select("id")
      .maybeSingle();

    if (profile) {
      profileId = profile.id;
    } else {
      // Most likely a unique clash on auth_user_id / phone from an earlier
      // profile — update that row instead so we never duplicate a lead.
      console.warn("[lead-identify-auth] upsert failed, falling back", upErr?.message);
      const { data: existing } = await supabaseAdmin
        .from("lead_profiles")
        .select("id")
        .eq("auth_user_id", user.id)
        .limit(1)
        .maybeSingle();
      if (!existing) return { ok: false as const, error: "persist_failed" as const };
      await supabaseAdmin
        .from("lead_profiles")
        .update(patch as never)
        .eq("id", existing.id);
      profileId = existing.id;
    }

    // Attach the pre-signup anonymous journey to the now-identified lead.
    try {
      await supabaseAdmin
        .from("lead_events")
        .update({ lead_profile_id: profileId } as never)
        .eq("anonymous_id", data.anonymousId)
        .is("lead_profile_id", null);
    } catch (err) {
      console.warn("[lead-identify-auth] event backfill failed", (err as Error).message);
    }

    try {
      await supabaseAdmin
        .from("product_engagement_visits")
        .update({ lead_profile_id: profileId } as never)
        .eq("anonymous_id", data.anonymousId)
        .is("lead_profile_id", null);
    } catch (err) {
      console.warn("[lead-identify-auth] engagement backfill failed", (err as Error).message);
    }

    // Bridge into the Customer Intelligence engine — this is the path that
    // finally links auth users onto canonical CI profiles.
    try {
      const { bridgeLeadToProfile } = await import(
        "@/lib/customer-intelligence/reconcile.server"
      );
      await bridgeLeadToProfile({
        anonymousId: data.anonymousId,
        authUserId: user.id,
        email,
        phone,
        reason: "identify_lead_auth",
      });
    } catch (err) {
      console.warn("[lead-identify-auth] ci bridge failed", (err as Error).message);
    }

    return { ok: true as const, leadProfileId: profileId };
  });

